import { zodResolver } from '@hookform/resolvers/zod';
import { FirebaseError } from 'firebase/app';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import logo from '@/assets/logo.png';
import { authErrorMessage } from '@/lib/auth/auth-errors';
import { GOOGLE_ENABLED, sendReset, signInWithGoogle, signInWithPassword } from '@/lib/auth/sign-in';
import { useAuth } from '@/lib/auth/use-auth';
import { Button, FormField, TextInput, toast } from '@/lib/ui';
import { useUiStore } from '@/app/ui-store';
import { APP_NAME } from '@shared/config';

const schema = z.object({
  identifier: z.string().trim().min(1, 'Enter your username or email.'),
  password: z.string().min(1, 'Enter your password.'),
});
type FormValues = z.infer<typeof schema>;

/** Google "G" mark, per Google's sign-in branding guidance. */
function GoogleLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export function SignInPage({ googleEnabled = GOOGLE_ENABLED }: { googleEnabled?: boolean }) {
  const { denied, clearDenied } = useAuth();
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, getValues, formState: { errors, isSubmitting } } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const run = async (fn: () => Promise<void>) => {
    setError(null);
    clearDenied();
    try {
      await fn(); // RedirectIfAuthed sends the signed-in user to their return path
    } catch (e) {
      setError(e instanceof FirebaseError ? authErrorMessage(e.code) : e instanceof Error ? e.message : authErrorMessage(undefined));
    }
  };

  const onForgot = async () => {
    try {
      await sendReset(getValues('identifier'));
      toast.success('If that account exists, a reset email is on its way.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enter your username or email first.');
    }
  };

  return (
    <div className="auth-page">
      <div className="card auth-card">
        <div className="card-body">
          <div className="auth-brand">
            <img className="brand-logo-img auth-logo" src={logo} alt="Vets Relief Solutions LLC logo" />
            <div className="page-title">{APP_NAME}</div>
            <div className="page-subtitle">Sign in to continue</div>
          </div>
          {(error || denied) ? <div className="announcement warning" role="alert">{error ?? denied}</div> : null}
          <form onSubmit={handleSubmit((v) => run(() => signInWithPassword(v.identifier, v.password)))} noValidate>
            <FormField label="Username or email" htmlFor="identifier" error={errors.identifier?.message} required>
              <TextInput id="identifier" autoComplete="username" autoFocus {...register('identifier')} />
            </FormField>
            <FormField label="Password" htmlFor="password" error={errors.password?.message} required>
              <TextInput id="password" type="password" autoComplete="current-password" {...register('password')} />
            </FormField>
            <Button type="submit" variant="primary" size="lg" loading={isSubmitting} style={{ width: '100%' }}>Sign in</Button>
          </form>
          {googleEnabled ? (
            <Button variant="secondary" size="lg" style={{ width: '100%', marginTop: 10, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }} onClick={() => run(signInWithGoogle)}>
              <GoogleLogo /> Sign in with Google
            </Button>
          ) : null}
          <div className="auth-links">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onForgot}>Forgot password?</button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={toggleTheme}>Toggle theme</button>
          </div>
          <p className="auth-footer">Vets Relief Solutions LLC · <Link to="/">itsm.vetsrelief.com</Link></p>
        </div>
      </div>
    </div>
  );
}
